import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition, ensureAgentHubBrands } from '~/consts/agentHub';

import { useGetPlaceNamesByIds } from '~/hooks/apis/search/useGetPlaceNamesByIds';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import SearchService, { TypeaheadType } from '~/services/SearchService';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

const LOCATION_TYPE_OPTIONS: Array<{ label: string; value: TypeaheadType }> = [
  { label: 'City', value: 'city' },
  { label: 'Country', value: 'country' },
];

/**
 * Location rule condition only (`rules.location`). Flight departure/arrival use
 * {@link AgentHubCommissionsFlightLocationInput}.
 */
function AgentHubCommissionsLocationInput({ setCreatedConditions, existingConditions }: Props) {
  const { tenant } = useCurrentTenant();
  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('idle');
  const [result, setResult] = useState<CommissionOption>([]);
  const [selectedLocations, setSelectedLocations] = useState<CommissionOption>(existingConditions || []);
  const [selectedLocationTypes, setSelectedLocationTypes] = useState(LOCATION_TYPE_OPTIONS);
  const [noOptionsText, setNoOptionsText] = useState<string>('Start typing to search for locations');
  const selectedLocationPlaceIds = useMemo(
    () => selectedLocations.map((location) => String(location.value)).filter(Boolean),
    [selectedLocations],
  );
  const { data: placeNames = {} } = useGetPlaceNamesByIds(selectedLocationPlaceIds);

  const debounceGetLocations = useMemo(
    () =>
      debounce((search: string, selectedTypes: Array<TypeaheadType>) => {
        if (!search) {
          setResult([]);
          return;
        }
        if (selectedTypes.length === 0) {
          setResult([]);
          setNoOptionsText('Select at least one location type');
          return;
        }
        setLoadingState('loading');
        SearchService.typeahead(search, {
          type: selectedTypes,
          brand: ensureAgentHubBrands(tenant.brand),
          region: 'AU',
          limit: 10,
          vertical: 'hotel',
          priority: 'current',
        })
          .then((response) => {
            setResult(
              response.result.map((location) => ({
                value: location.id,
                label: location.format.mainText,
              })),
            );
            setLoadingState('success');
          })
          .catch(() => {
            setResult([]);
            setNoOptionsText('Failed to fetch location');
          });
      }, 300),
    [tenant.brand],
  );

  useEffect(() => {
    setCreatedConditions(
      {
        type: 'location',
        value: selectedLocations,
      },
      true,
    );
  }, [selectedLocations, setCreatedConditions]);

  useEffect(() => {
    setSelectedLocations((currentLocations) => {
      let hasChanged = false;
      const nextLocations = currentLocations.map((location) => {
        const placeId = String(location.value);
        const label = placeNames[placeId] ?? location.label;

        if (label === location.label) {
          return location;
        }

        hasChanged = true;
        return { ...location, label };
      });

      return hasChanged ? nextLocations : currentLocations;
    });
  }, [placeNames]);

  const handleInputChange = (_: SyntheticEvent, newInputValue: string) => {
    if (!newInputValue) {
      setNoOptionsText('Start typing to search for locations');
      return;
    }
    debounceGetLocations(
      newInputValue,
      selectedLocationTypes.map((locationType) => locationType.value),
    );
  };

  const handleChange = (_: SyntheticEvent, value: CommissionOption) => {
    setSelectedLocations(value);
  };

  const handleLocationTypesChange = (_: SyntheticEvent, value: typeof LOCATION_TYPE_OPTIONS) => {
    setSelectedLocationTypes(value);
    setResult([]);
    setNoOptionsText(value.length === 0 ? 'Select at least one location type' : 'Start typing to search for locations');
    debounceGetLocations.cancel();
  };

  return (
    <Stack spacing={2}>
      <Autocomplete
        multiple
        options={LOCATION_TYPE_OPTIONS}
        getOptionLabel={(option) => option.label}
        value={selectedLocationTypes}
        filterSelectedOptions
        disableCloseOnSelect
        disablePortal
        renderInput={(params) => <TextField {...params} label="Location type" placeholder="Location type" />}
        onChange={handleLocationTypesChange}
      />
      <Box>
        <Autocomplete
          multiple
          loading={loadingState === 'loading'}
          options={result}
          getOptionLabel={(option) => option.label}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} label="Location" placeholder={selectedLocations.length > 0 ? '' : 'Location'} />
          )}
          onInputChange={handleInputChange}
          value={selectedLocations}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for locations
        </Typography>
      </Box>
    </Stack>
  );
}

export default AgentHubCommissionsLocationInput;
